import moment from 'moment';
import {DatePickerIntervalLabel, DateStringFormat, IntervalDate} from '@app/shared/_interfaces/dates';
import {defaultAnalyticsDateStringFormat} from '@config/config';

const getInterval = (interval: DatePickerIntervalLabel): IntervalDate => {
  let start, end;
  if (interval === 'last_month') {
    start = moment().subtract(1, 'month').startOf('month').toDate();
    end = moment().subtract(1, 'month').endOf('month').toDate();
  } else if (interval === 'this_month') {
    start = moment().startOf('month').toDate();
    end = moment().toDate();
  } else if (interval === 'last_3_months') {
    start = moment().subtract(3, 'month').toDate();
    end = moment().toDate();
  } else {
    start = moment().startOf('year').toDate();
    end = moment().toDate();
  }
  return [start, end];
};

export const getDateFromIntervals = (interval: DatePickerIntervalLabel, date: [number, number]): IntervalDate => {
  const [start, end] = date;
  if (start === -1 && end === -1) {
    return getInterval(interval);
  }
  return [moment().subtract(end, 'day').toDate(), moment().subtract(start, 'day').toDate()];
};


export const getMomentDate = (date: string) => {
  const momentDate = moment(date);
  return momentDate.isValid() ? momentDate : null;
};

export const getDateFormat = () => {
  return moment.locale() === 'it' ?
    'DD-MM-YYYY HH:mm:ss' :
    'YYYY-MM-DD HH:mm:ss';
};

export const getDateFormatNoSeconds = () => {
  return moment.locale() === 'it' ?
    'DD-MM-YYYY HH:mm' :
    'YYYY-MM-DD HH:mm';
};


export const getDateFormatNoHours = () => {
  return moment.locale() === 'it' ?
    'DD-MM-YYYY' :
    'YYYY-MM-DD';
};

export const getIntervalDateFormat = () => {
  return moment.locale() === 'it' ?
    'dd-mm-yy' :
    'yy-mm-dd';
};

export const getMomentFileName = (name: string, ext: 'csv') => {
  const momentDate = moment();
  return `${name}-${momentDate.format("YYYY-MM-DD")}.${ext}`
}

export const getMomentDateString = (date: string, forceFormat?: string) => {
  if (forceFormat) {
    return moment(date).utc(false).format(forceFormat);
  }
  return moment(date).utc(false).format(getDateFormat());
};

export const dateToString = (date: Date): string => moment(date).format('YYYY-MM-DD');
export const getMomentUnixDate = (date: number) => moment.unix(date);
export const unixDateToString = (date: number, format: string = 'YYYY-MM-DD', utc: boolean = true): string => getMomentUnixDate(date).utc(utc).format(format);

export const getStringFromIntervals = (dates: IntervalDate) => {
  return dates.map(dateToString).filter(d => moment(d).utc(false).isValid());
};

export const getMomentMeta = (date: Date) => {
  const momentDate = moment(date);
  return {day: momentDate.day(), month: momentDate.month(), year: momentDate.year()};
};

export const getTimezone = () => moment(new Date()).format('Z');

export const getDifferenceDays = (start: string, end: string) : number => {
  return Math.abs(moment(end).diff(start, 'days'));
};

export const convertTime = (milliseconds: number, format?: DateStringFormat): string => {

  if (!format) {
    format = defaultAnalyticsDateStringFormat;
  } else {
    format = {...defaultAnalyticsDateStringFormat, ...format};
  }

  if (milliseconds < 1000) {
    return `0${format.s}`;
  }

  const m = moment.duration(milliseconds, 'milliseconds'),
    hours = Math.floor(m.hours()),
    seconds = Math.floor(m.seconds()),
    minutes = Math.floor(m.minutes());

  return `${hours > 0 ? hours + `${format.h} ` : ''} ${minutes > 0 ? minutes + `${format.m} ` : ''} ${seconds > 0 ? seconds + `${format.s}` : ''}`;

};

export const setLocale = (locale: string) => {
  if (locale !== 'ar'){
    moment.locale(locale);
  }
};

export const getLocale = () => moment.locale();

// End of today, not the exact current instant: preset ranges compute their
// "to" as `moment()` (now, at click time), while maxDate is captured earlier
// (page load). The datepicker blanks any value past maxDate from the input
// field, so the "to" — a few seconds ahead of maxDate — gets dropped and only
// the start shows. endOf('day') keeps today fully selectable and the end visible.
export const getMaxDate = () => moment().endOf('day').toDate();
